import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';
import { applyThemeToDocument } from '../../utils/theme';
import toast from 'react-hot-toast';

const ThemeSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  const colorSettings = [
    { key: 'color_primary', label: 'Couleur principale', description: 'Jaune SunServ, boutons, liens' },
    { key: 'color_secondary', label: 'Couleur secondaire', description: 'Éléments secondaires' },
    { key: 'color_background', label: 'Fond', description: 'Couleur de fond du site' },
    { key: 'color_surface', label: 'Surfaces', description: 'Cards, modales, surfaces' },
    { key: 'color_text', label: 'Texte principal', description: 'Texte principal' },
    { key: 'color_text_secondary', label: 'Texte secondaire', description: 'Texte de description, métadonnées' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data.success) {
        setSettings(response.data.settings);
        applyThemeToDocument(response.data.settings);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      const response = await api.put('/settings', { settings });
      if (response.data.success) {
        toast.success('Thème enregistré');
        applyThemeToDocument(settings);
        console.log('Thème appliqué:', settings);
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur de sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (key) => {
    if (!confirm('Réinitialiser cette couleur ?')) return;
    
    try {
      const response = await api.post(`/settings/reset/${key}`);
      if (response.data.success) {
        setSettings({ ...settings, [key]: response.data.value });
        toast.success('Couleur réinitialisée');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <AdminLayout>
      <Helmet><title>Thème - Admin Blog</title></Helmet>
      
      <div className="p-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Personnalisation du thème</h1>
          <p className="text-gray-400">Modifiez les couleurs de votre blog</p>
        </div>

        <div className="bg-surface rounded-xl p-6 border border-gray-700/30 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Couleurs</h2>
          
          <div className="space-y-6">
            {colorSettings.map(setting => (
              <div key={setting.key} className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-white font-medium mb-1">{setting.label}</label>
                  <p className="text-gray-400 text-sm">{setting.description}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-gray-700"
                    style={{ backgroundColor: settings[setting.key] }}
                  ></div>
                  <input
                    type="color"
                    value={settings[setting.key] || '#000000'}
                    onChange={(e) => setSettings({ ...settings, [setting.key]: e.target.value })}
                    className="w-20 h-12 bg-surface/90 border border-gray-700 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings[setting.key] || ''}
                    onChange={(e) => setSettings({ ...settings, [setting.key]: e.target.value })}
                    className="w-28 px-3 py-2 bg-surface/90 border border-gray-700 rounded-lg text-white text-sm"
                    placeholder="#FACC15"
                  />
                  <button
                    onClick={() => handleReset(setting.key)}
                    className="px-4 py-2 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-700 text-sm"
                  >
                    <i className="fas fa-undo"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Aperçu */}
        <div className="bg-surface rounded-xl p-6 border border-gray-700/30 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Aperçu</h2>
          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: settings.color_background }}>
              <h3 style={{ color: settings.color_text }}>Titre principal</h3>
              <p style={{ color: settings.color_text_secondary }}>Texte secondaire</p>
              <button
                className="mt-2 px-6 py-2 rounded-lg font-semibold"
                style={{
                  backgroundColor: settings.color_primary,
                  color: '#0F1419'
                }}
              >
                Bouton principal
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : 'Enregistrer le thème'}
          </button>
          <button
            onClick={loadSettings}
            className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg"
          >
            Annuler
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ThemeSettings;
